<template>
  <div class="space-y-2 text-[11px] text-gray-700">
    <template v-if="mode === 'signal'">
      <div class="flex items-center gap-3">
        <label class="inline-flex items-center gap-1 cursor-pointer">
          <input v-model="state" type="radio" value="resolved" /> Resolved
        </label>
        <label class="inline-flex items-center gap-1 cursor-pointer">
          <input v-model="state" type="radio" value="no_longer_relevant" /> No longer relevant
        </label>
      </div>
    </template>

    <template v-if="mode === 'todo'">
      <div class="flex items-center gap-x-3 gap-y-1 flex-wrap">
        <label v-for="o in TODO_OUTCOMES" :key="o.key" class="inline-flex items-center gap-1 cursor-pointer" :title="o.hint">
          <input v-model="todoOutcome" type="radio" :value="o.key" /> {{ o.label }}
        </label>
      </div>
    </template>

    <template v-if="mode === 'signal' && state === 'resolved'">
      <div class="flex items-center gap-x-3 gap-y-1 flex-wrap">
        <span class="text-gray-500">How complete:</span>
        <label v-for="g in GRADES" :key="g.key" class="inline-flex items-center gap-1 cursor-pointer" :title="g.hint">
          <input v-model="grade" type="radio" :value="g.key" /> {{ g.label }}
        </label>
      </div>
    </template>

    <template v-if="needsTitle">
      <input
        v-model="title"
        type="text"
        :placeholder="mode === 'todo' && todoOutcome === 'unable' ? 'What was tried' : 'What was done'"
        class="w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
    </template>
    <textarea
      v-if="mode !== 'acknowledge'"
      v-model="summary"
      rows="2"
      :placeholder="summaryPlaceholder"
      class="w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
    ></textarea>

    <div class="rounded border border-gray-200 bg-gray-50/60 p-2 space-y-1.5">
      <div class="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        Evidence<span v-if="mode === 'acknowledge'" class="normal-case font-normal tracking-normal"> (optional)</span>
      </div>
      <textarea
        v-model="evidenceText"
        rows="2"
        placeholder="What the patient said, what was documented, or what you confirmed, as close to verbatim as you can"
        class="w-full border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
      ></textarea>
      <div class="flex items-center gap-2 flex-wrap">
        <select v-model="evidenceType" class="border border-gray-300 rounded px-1.5 py-1 bg-white">
          <option v-for="t in EVIDENCE_TYPES" :key="t.key" :value="t.key">{{ t.label }}</option>
        </select>
        <span class="text-gray-500">on</span>
        <input v-model="evidenceDate" type="date" :max="today" class="border border-gray-300 rounded px-1.5 py-0.5 bg-white" />
      </div>
    </div>

    <div v-if="openTodoCount && mode === 'signal'" class="text-gray-500">
      <template v-if="state === 'resolved'">This also closes {{ openTodoCount }} open to-do{{ openTodoCount === 1 ? '' : 's' }} with this record.</template>
      <template v-else>This hides {{ openTodoCount }} open to-do{{ openTodoCount === 1 ? '' : 's' }}. Reopening brings them back.</template>
    </div>
    <div v-if="error" class="text-red-600">{{ error }}</div>

    <div class="flex items-center gap-2">
      <button
        class="px-2 py-1 rounded text-[11px] font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
        :disabled="!canSubmit || saving"
        @click="submit"
      >{{ submitLabel }}</button>
      <button class="px-2 py-1 rounded text-[11px] text-gray-500 hover:text-gray-700" @click="$emit('cancel')">Cancel</button>
    </div>
  </div>
</template>

<script>
const TODO_OUTCOMES = [
  { key: 'verified_terminal', label: 'Done', hint: 'The terminal act happened: filled, attended, active, contacted with a disposition.' },
  { key: 'in_progress_step', label: 'Step taken', hint: 'A real step that does not finish it: referred, booked, prior-auth submitted.' },
  { key: 'soft_action', label: 'Advice only', hint: 'Education, advice or monitoring only.' },
  { key: 'unable', label: "Couldn't complete", hint: 'The team tried and could not.' },
];
const GRADES = TODO_OUTCOMES.filter(o => o.key !== 'unable');
const EVIDENCE_TYPES = [
  { key: 'note', label: 'Note' },
  { key: 'transcript_quote', label: 'Call / conversation' },
  { key: 'assessment_answer', label: 'Assessment answer' },
  { key: 'chart_event', label: 'Chart / EHR' },
];
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default {
  name: 'CareGraphCloseForm',
  props: {
    mode: { type: String, required: true },
    defaultTitle: { type: String, default: '' },
    openTodoCount: { type: Number, default: 0 },
    saving: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },
  emits: ['submit', 'cancel'],
  data() {
    return {
      TODO_OUTCOMES,
      GRADES,
      EVIDENCE_TYPES,
      today: localToday(),
      state: 'resolved',
      todoOutcome: 'verified_terminal',
      grade: 'verified_terminal',
      title: this.defaultTitle,
      summary: '',
      evidenceText: '',
      evidenceType: 'note',
      evidenceDate: localToday(),
    };
  },
  computed: {
    needsTitle() {
      return this.mode === 'todo' || (this.mode === 'signal' && this.state === 'resolved');
    },
    summaryPlaceholder() {
      if (this.mode === 'signal' && this.state === 'no_longer_relevant') return 'Why it no longer applies (required)';
      return 'Details (optional)';
    },
    submitLabel() {
      if (this.mode === 'acknowledge') return 'Acknowledge';
      if (this.mode === 'signal') return this.state === 'resolved' ? 'Close as resolved' : 'Close as no longer relevant';
      return 'Close to-do';
    },
    canSubmit() {
      if (this.mode === 'acknowledge') return true;
      if (!this.evidenceText.trim() || !this.evidenceDate) return false;
      if (this.needsTitle && !this.title.trim()) return false;
      if (this.mode === 'signal' && this.state === 'no_longer_relevant' && !this.summary.trim()) return false;
      return true;
    },
  },
  methods: {
    submit() {
      const evidence = this.evidenceText.trim()
        ? { text: this.evidenceText.trim(), evidence_type: this.evidenceType, evidence_date: this.evidenceDate }
        : null;
      if (this.mode === 'acknowledge') {
        this.$emit('submit', { acknowledge: true, evidence });
        return;
      }
      if (this.mode === 'signal') {
        const payload = { state: this.state, summary: this.summary.trim(), evidence };
        if (this.state === 'resolved') Object.assign(payload, { title: this.title.trim(), closure_grade: this.grade });
        this.$emit('submit', payload);
        return;
      }
      const unable = this.todoOutcome === 'unable';
      this.$emit('submit', {
        outcome: unable ? 'unable_to_complete' : 'done',
        closure_grade: unable ? null : this.todoOutcome,
        title: this.title.trim(),
        summary: this.summary.trim(),
        evidence,
      });
    },
  },
};
</script>
